import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TicketAction } from "./action-cards/TicketAction";
import { useCallback, useMemo } from "react";

export interface TicketActionsProps {
  ticketId: string;
  ticket?: TellescopeModelFromName<"tickets">;
}

export const TicketActions = ({ ticketId, ticket: providedTicket }: TicketActionsProps) => {
  const { t } = useTranslation("tickets");
  const ticketQuery = useTellescopeQuery({
    model: "tickets",
    method: "getOne",
    request: ticketId,
    enabled: !!ticketId && !providedTicket,
  });

  const updateTicketMutation = useTellescopeMutation({
    model: "tickets",
    method: "updateOne",
  });

  const ticket = providedTicket ?? ticketQuery.data;

  const actionsWithIds = useMemo(() => {
    return (
      ticket?.actions?.map((action) => {
        return {
          ...action,
          id: crypto.randomUUID(),
        };
      }, {}) ?? []
    );
  }, [ticket?.actions]);

  const onCompleteAction = useCallback(
    (actionId: string) => {
      updateTicketMutation.mutate({
        id: ticketId,
        actions: actionsWithIds.map(({ id, ...action }) =>
          id === actionId ? { ...action, completedAt: new Date() } : action
        ),
        _options: {
          replaceObjectFields: true,
        },
      });
    },
    [actionsWithIds, ticketId, updateTicketMutation]
  );

  if (!ticket?.actions?.length) {
    return (
      <div className="text-xs text-muted-foreground flex gap-1 items-center">
        <FileText className="w-3 h-3 text-muted-foreground shrink-0" />
        <p>{t("No actions")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {actionsWithIds.map(({ id, ...action }) => {
        return (
          <TicketAction
            key={JSON.stringify(action)}
            action={action}
            onCompleteAction={() => onCompleteAction(id)}
            enduserId={ticket?.enduserId}
          />
        );
      })}
    </div>
  );
};
